import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  LocalShipping as ReceiveIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { relativeDate } from '../../../../utils/dateUtils';
import { useVenue } from '../../../../context/VenueContext';
import PageHeader from '../../../../components/venue/PageHeader';
import FilterBar from '../../../../components/venue/FilterBar';
import EmptyState from '../../../../components/venue/EmptyState';
import inventoryDashboardService from '../../../../services/inventoryDashboardService';
import inventoryCategoryService from '../../../../services/inventoryCategoryService';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
// ── Help modal — remove the next import + all helpOpen state + <InventoryHelpModal> to disable ──
import InventoryHelpModal from '../../../../components/venue/InventoryHelpModal';
import ReservedItemsCard from '../../../../components/venue/ReservedItemsCard';
import type { InventoryDashboard as InventoryDashboardData, DashboardMovementRow, InventoryCategory, InventoryItemType } from '../../../../types/venue';
import { humanizeItemType, ITEM_TYPE_FILTER_OPTIONS } from '../../../../utils/inventoryItemDisplay';
import { formatQty, formatCardQty } from '../../../../utils/quantityFormat';

// ── Helpers ───────────────────────────────────────────────────────────────────

function signedQty(qty: number): string {
  return qty > 0 ? `+${formatQty(qty)}` : formatQty(qty);
}

function qtyColor(qty: number): string {
  if (qty > 0) return '#1a7f37';
  if (qty < 0) return '#cf222e';
  return '#6e7781'; // neutral — matches text.secondary in both themes
}

function fmtBottleSize(ml: number | null | undefined): string {
  if (ml == null) return '';
  return ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | null;
  color?: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, color, onClick }) => {
  const theme = useTheme();
  const isClickable = !!onClick;
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        flex: '1 1 160px',
        minWidth: 140,
        cursor: isClickable ? 'pointer' : 'default',
        transition: isClickable ? 'box-shadow 0.15s, transform 0.1s' : undefined,
        '&:hover': isClickable ? {
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          transform: 'translateY(-1px)',
        } : undefined,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{
          color: theme.palette.text.secondary,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          display: 'block',
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        fontWeight={700}
        sx={{ color: color ?? theme.palette.text.primary }}
      >
        {formatCardQty(value)}
      </Typography>
    </Paper>
  );
};

// ── Section header ────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  icon: React.ReactNode;
  title: string;
  badge?: number | null;
  action?: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ icon, title, badge, action }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color: theme.palette.text.secondary, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
        {badge != null && (
          <Typography variant="caption" color="text.secondary">({badge})</Typography>
        )}
      </Box>
      {action}
    </Box>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { venueId } = useVenue();

  const [data, setData] = useState<InventoryDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false); // ← remove with InventoryHelpModal

  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string | 'none' | ''>('');
  const [typeFilter, setTypeFilter] = useState<InventoryItemType | ''>('');

  // Load active categories once (for the dynamic category filter).
  useEffect(() => {
    if (!venueId) return;
    inventoryCategoryService.getCategories(venueId, {})
      .then(setCategories)
      .catch(() => { /* non-blocking */ });
  }, [venueId]);

  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    inventoryDashboardService.getDashboard(venueId, categoryFilter || undefined, typeFilter || undefined)
      .then(setData)
      .catch(() => setError('Failed to load inventory dashboard. Please try again.'))
      .finally(() => setLoading(false));
  }, [venueId, categoryFilter, typeFilter]);

  const inventoryBase = `/admin/venues/${venueId}/inventory`;

  // ── Deep-link helpers: include active category/type filter when navigating to inventory list ─
  const listLink = (extra: string) => {
    const params = new URLSearchParams();
    if (extra) params.set('status', extra);
    if (categoryFilter) params.set('categoryId', categoryFilter);
    if (typeFilter) params.set('type', typeFilter);
    const qs = params.toString();
    return `${inventoryBase}${qs ? `?${qs}` : ''}`;
  };

  const renderMovementRow = (row: DashboardMovementRow, index: number) => {
    return (
      <TableRow key={`${row.inventoryItemId}-${index}`} hover>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.displayName || '—'}
          </Typography>
          {row.bottleSize != null && (
            <Typography variant="caption" color="text.secondary">
              {fmtBottleSize(row.bottleSize)}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <MovementTypeBadge type={row.type} />
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ color: qtyColor(row.quantity), fontFamily: 'monospace' }}
          >
            {signedQty(row.quantity)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary">
            {row.locationName ?? '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            {relativeDate(row.postedAt)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  const renderReceiveRow = (row: DashboardMovementRow, index: number) => {
    const sizeAndLocation = [fmtBottleSize(row.bottleSize), row.locationName].filter(Boolean).join(' · ');
    return (
      <TableRow key={`recv-${row.inventoryItemId}-${index}`} hover>
        <TableCell>
          <Typography variant="body2" fontWeight={500}>
            {row.displayName || '—'}
          </Typography>
          {sizeAndLocation && (
            <Typography variant="caption" color="text.secondary">
              {sizeAndLocation}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600} sx={{ color: '#1a7f37' }}>
            +{formatQty(row.quantity)}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
            {relativeDate(row.postedAt)}
          </Typography>
        </TableCell>
      </TableRow>
    );
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<InventoryIcon />}
        title="Inventory Overview"
        onHelp={() => setHelpOpen(true)}
        helpTitle="How to use this page"
        actions={
          <>
            <Button
              size="small"
              onClick={() => navigate(`/admin/venues/${venueId}/ownership-accounts`)}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
            >
              Ownership Accounts
            </Button>
            <Button
              size="small"
              onClick={() => navigate(`/admin/venues/${venueId}/ownership-reports`)}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8rem' }}
            >
              Consignment Reports
            </Button>
            <Button
              size="small"
              onClick={() => navigate(`${inventoryBase}/valuation`)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              Stock estimate
            </Button>
          </>
        }
      />

      {/* ── Filters ──────────────────────────────────────────────────── */}
      <FilterBar>
        {categories.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={e => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All categories</MenuItem>
              {categories.map(c => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
              <MenuItem value="none">— Unassigned —</MenuItem>
            </Select>
          </FormControl>
        )}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={e => setTypeFilter(e.target.value as InventoryItemType | '')}
          >
            <MenuItem value="">All types</MenuItem>
            {ITEM_TYPE_FILTER_OPTIONS.map(t => (
              <MenuItem key={t} value={t}>{humanizeItemType(t)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      {/* ── Stat cards ─────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error || !data ? (
        <Alert severity="error">{error ?? 'No data available.'}</Alert>
      ) : (
      <>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <StatCard label="Total Items" value={data.totalItems} />
        <StatCard label="Total Bottles" value={data.totalBottles} />
        <StatCard
          label="Low Stock"
          value={data.lowStockCount}
          color={data.lowStockCount > 0 ? '#9a6700' : undefined}
          onClick={() => navigate(listLink('LOW'))}
        />
        <StatCard
          label="Out of Stock"
          value={data.outOfStockCount}
          color={data.outOfStockCount > 0 ? '#cf222e' : undefined}
          onClick={() => navigate(listLink('OUT'))}
        />
      </Box>

      {/* ── Received last 7 days ───────────────────────────────────────── */}
      <Box>
        <SectionHeader
          icon={<ReceiveIcon fontSize="small" />}
          title="Received last 7 days"
          badge={data.recentlyReceived.count}
          action={
            <Button
              size="small"
              onClick={() => navigate(`${inventoryBase}/movements?type=RECEIVE`)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              View all
            </Button>
          }
        />
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          {data.recentlyReceived.items.length === 0 ? (
            <EmptyState icon={ReceiveIcon} message="No deliveries received in the last 7 days." />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Wine', 'Qty', 'Date'].map(h => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recentlyReceived.items.map(renderReceiveRow)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* ── Recent activity ────────────────────────────────────────────── */}
      <Box>
        <SectionHeader
          icon={<InventoryIcon fontSize="small" />}
          title="Recent activity"
          badge={data.recentMovements.length > 0 ? data.recentMovements.length : null}
          action={
            <Button
              size="small"
              onClick={() => navigate(`${inventoryBase}/movements`)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              View all
            </Button>
          }
        />
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }}>
          {data.recentMovements.length === 0 ? (
            <EmptyState icon={InventoryIcon} message="No movements recorded yet." />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Wine', 'Type', 'Qty', 'Location', 'Date'].map(h => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.recentMovements.map(renderMovementRow)}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* ── Currently reserved ─────────────────────────────────────────── */}
      <Box>
        <SectionHeader
          icon={<WarningIcon fontSize="small" />}
          title="Currently reserved"
          badge={null}
          action={
            <Button
              size="small"
              onClick={() => navigate(`${inventoryBase}?status=RESERVED`)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              View all
            </Button>
          }
        />
        <ReservedItemsCard venueId={venueId} />
      </Box>
      </>
      )}

    </Box>

    {/* ── Help modal — remove this block + helpOpen state + HelpIcon import to disable ── */}
    <InventoryHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default InventoryDashboard;
